import { useEffect, useState } from "react";
import { CURRENTLY, PAST_LIFE, PROFILE, PROJECTS, SKILL_GROUPS, SOCIALS } from "./data";

/**
 * A Windows-shaped filesystem. The OS is the product here — the portfolio is
 * just what happens to be saved on this machine, the way anyone's documents
 * live on theirs.
 *
 *   This PC
 *     └── Local Disk (C:)
 *           ├── Program Files, Windows, PerfLogs
 *           └── Users\earlpappi\{Desktop, Documents, Downloads, Music,
 *                                Pictures, Videos}
 *
 * `open` says which app handles the file, matching Windows' file associations.
 */

let seq = 0;
const uid = (p) => `${p}${++seq}`;

const dir = (name, children = [], extra = {}) => ({
  id: uid("d"),
  name,
  type: "folder",
  kind: "File folder",
  modified: "2026-06-18 09:12",
  children,
  ...extra,
});

const doc = (name, extra = {}) => ({
  id: uid("f"),
  name,
  type: "file",
  kind: "Text Document",
  ext: "txt",
  modified: "2026-06-18 09:12",
  size: "3 KB",
  ...extra,
});

/* --------------------------------------------------------- document text */

const rule = (title) => `${title}\n${"=".repeat(title.length)}\n`;

const ABOUT_TXT = `${rule("About me")}
${PROFILE.name} (${PROFILE.handle})
${PROFILE.role} — ${PROFILE.location}

${PROFILE.bio}

Currently a frontend engineer at CapitalSage and a postgraduate computer
science student at Babcock University. Previously led frontend teams at
Pureworker, African Founders and Forenxy.

Email    ${PROFILE.email}
Phone    ${PROFILE.phone}
GitHub   github.com/${PROFILE.github}
`;

const EXPERIENCE_TXT = `${rule("Experience")}
CURRENTLY
${CURRENTLY.map(
  (c) => `\n  ${c.title}\n  ${c.meta}\n    ${c.description}`
).join("\n")}

PAST LIFE
${PAST_LIFE.map(
  (p) => `\n  ${p.title}\n  ${p.meta}\n    ${p.description}`
).join("\n")}
`;

const SKILLS_TXT = `${rule("Skills")}
${SKILL_GROUPS.map(
  (g) => `${g.label}\n${g.items.map((i) => `  - ${i}`).join("\n")}`
).join("\n\n")}
`;

const CONTACT_TXT = `${rule("Contact")}
${PROFILE.name}
${PROFILE.role}
${PROFILE.location}

Email     ${PROFILE.email}
Phone     ${PROFILE.phone}
${SOCIALS.map((s) => `${s.label.padEnd(9)} ${s.url}`).join("\n")}

Open to freelance fullstack and frontend work.
`;

const README_TXT = `${rule("Read me first")}
Welcome — this desktop is a browser-based Windows 11 replica.

  * Double-click anything on the desktop or in File Explorer.
  * Everything about me lives in Documents\\Portfolio.
  * Projects are internet shortcuts in Documents\\Projects — they open in Edge.
  * Settings > Personalisation changes the theme, accent colour and wallpaper.
  * Terminal understands dir, cd, type, tree, start and more. Try "help".

Windows and the apps here are recreated in HTML and CSS. Nothing is installed.
`;

/* --------------------------------------------------------------- the tree */

const PROJECT_LINKS = PROJECTS.map((p) =>
  doc(`${p.title.replace(/[^\w.() -]/g, "")}.url`, {
    kind: "Internet Shortcut",
    ext: "url",
    size: "1 KB",
    modified: `${p.year}-11-04 14:21`,
    open: { app: "edge", url: p.url },
    summary: `${p.kind} · ${p.year} — ${p.description}`,
  })
);

const SOCIAL_LINKS = SOCIALS.map((s) =>
  doc(`${s.label}.url`, {
    kind: "Internet Shortcut",
    ext: "url",
    size: "1 KB",
    open: { app: "edge", url: s.url },
    summary: s.handle,
  })
);

const PORTFOLIO_DIR = dir("Portfolio", [
  doc("About Me.txt", { text: ABOUT_TXT, open: { app: "notepad" } }),
  doc("Experience.txt", { text: EXPERIENCE_TXT, open: { app: "notepad" }, size: "6 KB" }),
  doc("Skills.txt", { text: SKILLS_TXT, open: { app: "notepad" }, size: "2 KB" }),
  doc("Contact.txt", { text: CONTACT_TXT, open: { app: "notepad" } }),
]);

const PROJECTS_DIR = dir("Projects", PROJECT_LINKS);
const LINKS_DIR = dir("Links", SOCIAL_LINKS);

const RESUME_PDF = doc("Resume.pdf", {
  kind: "Microsoft Edge PDF Document",
  ext: "pdf",
  size: "184 KB",
  modified: "2026-06-02 17:40",
  open: { app: "edge", url: `${process.env.PUBLIC_URL || ""}/miracle_aponjolosun.pdf` },
});

const PROFILE_PNG = doc("Profile.png", {
  kind: "PNG File",
  ext: "png",
  size: "96 KB",
  open: { app: "photos", src: PROFILE.avatar },
});

const DOCUMENTS = dir(
  "Documents",
  [PORTFOLIO_DIR, PROJECTS_DIR, LINKS_DIR, RESUME_PDF],
  { special: "documents" }
);

const DESKTOP = dir(
  "Desktop",
  [
    doc("Read me first.txt", { text: README_TXT, open: { app: "notepad" } }),
    { ...RESUME_PDF, id: uid("f"), shortcut: true },
    { ...PORTFOLIO_DIR, id: uid("d"), shortcut: true, target: PORTFOLIO_DIR.id },
    { ...PROJECTS_DIR, id: uid("d"), shortcut: true, target: PROJECTS_DIR.id },
  ],
  { special: "desktop" }
);

const DOWNLOADS = dir(
  "Downloads",
  [
    doc("miracle_aponjolosun.pdf", {
      kind: "Microsoft Edge PDF Document",
      ext: "pdf",
      size: "184 KB",
      open: {
        app: "edge",
        url: `${process.env.PUBLIC_URL || ""}/miracle_aponjolosun.pdf`,
      },
    }),
  ],
  { special: "downloads" }
);

const PICTURES = dir("Pictures", [PROFILE_PNG], { special: "pictures" });
const MUSIC = dir("Music", [], { special: "music" });
const VIDEOS = dir("Videos", [], { special: "videos" });

const USER = dir("earlpappi", [
  DESKTOP,
  DOCUMENTS,
  DOWNLOADS,
  MUSIC,
  PICTURES,
  VIDEOS,
]);

export const DRIVE_C = {
  id: "drive-c",
  name: "Local Disk (C:)",
  type: "drive",
  kind: "Local Disk",
  letter: "C:",
  usedGb: 262,
  totalGb: 476,
  children: [
    dir("PerfLogs", []),
    dir("Program Files", [dir("Microsoft"), dir("Windows Defender")]),
    dir("Program Files (x86)", [dir("Common Files")]),
    dir("Users", [USER]),
    dir("Windows", [dir("System32"), dir("Fonts"), dir("Temp")]),
  ],
};

export const RECYCLE_BIN = {
  id: "recycle-bin",
  name: "Recycle Bin",
  type: "recycle",
  kind: "System Folder",
  children: [],
};

export const THIS_PC = {
  id: "this-pc",
  name: "This PC",
  type: "root",
  children: [DRIVE_C],
  quickFolders: [DESKTOP, DOCUMENTS, DOWNLOADS, MUSIC, PICTURES, VIDEOS],
};

export const HOME_FOLDERS = THIS_PC.quickFolders;

/** Handles for the documents other surfaces want to open directly. */
export const QUICK_FILES = {
  readme: DESKTOP.children[0],
  about: PORTFOLIO_DIR.children[0],
  experience: PORTFOLIO_DIR.children[1],
  skills: PORTFOLIO_DIR.children[2],
  contact: PORTFOLIO_DIR.children[3],
  resume: RESUME_PDF,
  portfolio: PORTFOLIO_DIR,
  projects: PROJECTS_DIR,
};
export const DESKTOP_DIR = DESKTOP;
export const DOCUMENTS_DIR = DOCUMENTS;

/* ------------------------------------------------------------- index/walk */

const index = new Map();
const trails = new Map();

(function walk(node, trail) {
  index.set(node.id, node);
  trails.set(node.id, trail);
  (node.children || []).forEach((child) => walk(child, [...trail, node]));
})(THIS_PC, []);

index.set(RECYCLE_BIN.id, RECYCLE_BIN);
trails.set(RECYCLE_BIN.id, []);

export const getNode = (id) => index.get(id) || THIS_PC;
export const getTrail = (id) => trails.get(id) || [];

/** Windows-style path string, e.g. C:\Users\earlpappi\Documents */
export function pathOf(id) {
  const trail = [...getTrail(id), getNode(id)];
  return trail
    .map((n) => (n.id === "this-pc" ? "" : n.type === "drive" ? n.letter : n.name))
    .filter(Boolean)
    .join("\\");
}

export function findByPathSegment(node, name) {
  return (node.children || []).find(
    (c) => c.name.toLowerCase() === name.toLowerCase()
  );
}

export function searchTree(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits = [];
  index.forEach((node) => {
    if (node.type === "root" || node.type === "drive") return;
    if (node.name.toLowerCase().includes(q)) hits.push(node);
  });
  return hits.slice(0, 60);
}

/* ==========================================================================
   Mutations — New, Delete, Restore, Empty Recycle Bin
   The tree is live, so context-menu actions are real rather than decorative.
   Components subscribe with useFsVersion() to re-render after a change.
   ========================================================================== */

let version = 0;
const listeners = new Set();

const emit = () => {
  version += 1;
  listeners.forEach((fn) => fn(version));
};

export const subscribeFs = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getFsVersion = () => version;

/** Registers a node (and its descendants) in the lookup tables. */
function register(node, trail) {
  index.set(node.id, node);
  trails.set(node.id, trail);
  (node.children || []).forEach((child) => register(child, [...trail, node]));
}

const stamp = () =>
  new Date().toISOString().slice(0, 16).replace("T", " ");

/** Unique name in a folder: "New folder", "New folder (2)", … */
function uniqueName(parent, base, ext = "") {
  const taken = new Set((parent.children || []).map((c) => c.name));
  let name = `${base}${ext}`;
  let n = 2;
  while (taken.has(name)) name = `${base} (${n++})${ext}`;
  return name;
}

export function createFolder(parentId) {
  const parent = getNode(parentId);
  if (!parent.children) return null;
  const node = {
    id: uid("d"),
    name: uniqueName(parent, "New folder"),
    type: "folder",
    kind: "File folder",
    modified: stamp(),
    children: [],
  };
  parent.children.push(node);
  register(node, [...getTrail(parent.id), parent]);
  emit();
  return node;
}

export function createTextFile(parentId) {
  const parent = getNode(parentId);
  if (!parent.children) return null;
  const node = {
    id: uid("f"),
    name: uniqueName(parent, "New Text Document", ".txt"),
    type: "file",
    kind: "Text Document",
    ext: "txt",
    size: "0 KB",
    modified: stamp(),
    text: "",
    open: { app: "notepad" },
  };
  parent.children.push(node);
  register(node, [...getTrail(parent.id), parent]);
  emit();
  return node;
}

/** Moves a node to the Recycle Bin, remembering where it came from. */
export function deleteNode(id) {
  const node = getNode(id);
  const trail = getTrail(id);
  const parent = trail[trail.length - 1];
  if (!node || !parent || node.protected) return false;
  parent.children = parent.children.filter((c) => c.id !== id);
  node.deletedFrom = parent.id;
  node.deletedAt = stamp();
  RECYCLE_BIN.children.push(node);
  register(node, [RECYCLE_BIN]);
  emit();
  return true;
}

export function restoreNode(id) {
  const node = getNode(id);
  if (!node?.deletedFrom) return false;
  const parent = getNode(node.deletedFrom);
  RECYCLE_BIN.children = RECYCLE_BIN.children.filter((c) => c.id !== id);
  parent.children.push(node);
  register(node, [...getTrail(parent.id), parent]);
  delete node.deletedFrom;
  delete node.deletedAt;
  emit();
  return true;
}

export function emptyRecycleBin() {
  RECYCLE_BIN.children.forEach((node) => index.delete(node.id));
  RECYCLE_BIN.children = [];
  emit();
}

/** Rough folder size, for the properties sheet. */
export function sizeOf(node) {
  if (!node) return 0;
  if (node.type === "file") {
    const kb = parseFloat(node.size) || 0;
    return node.size?.includes("MB") ? kb * 1024 : kb;
  }
  return (node.children || []).reduce((sum, c) => sum + sizeOf(c), 0);
}

export function countContents(node) {
  let files = 0;
  let folders = 0;
  (node.children || []).forEach((child) => {
    if (child.type === "file") files += 1;
    else {
      folders += 1;
      const inner = countContents(child);
      files += inner.files;
      folders += inner.folders;
    }
  });
  return { files, folders };
}

/** Re-renders a component whenever the filesystem changes. */
export function useFsVersion() {
  const [v, setV] = useState(getFsVersion);
  useEffect(() => subscribeFs(setV), []);
  return v;
}
